import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ExternalLink, Info, Loader2, Package, TriangleAlert, XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError } from "@hostdock/sdk";
import type { AppCatalogItem, AppConfigFieldInfo, AppInstallTarget } from "@hostdock/types";
import { Button, CatalogAppLogo, cn, ErrorState, Field, FormError, Input, KeyValue, Notice, Select, Skeleton } from "@hostdock/ui";
import { api } from "../api";
import { Banner, DetailCard, PageHero, Pill } from "../components/page-kit";
import { useLiveJob } from "../lib/live-job";
import { useSession } from "../session";
import { CATEGORY_LABEL } from "./apps";
import { Choice } from "./create-website";

const CAP_LABEL: Record<string, string> = {
  update: "Updates from HostDock", backup: "Backups", restore: "Restore from a backup", clone: "Clone", staging: "Staging copy", adminLogin: "One-click administrator sign-in", uninstall: "Clean uninstall", logs: "Installation logs",
};

function cmp(a: string, b: string) {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) - (pb[i] ?? 0);
  return 0;
}
const phpOk = (a: AppCatalogItem, php: string, min: string | null) => (!min || cmp(php, min) >= 0) && (!a.requirements.phpMax || cmp(php, a.requirements.phpMax) <= 0);

/** One app: what it is, what HostDock can do with it, and the install form. */
export function AppInstallPage() {
  const { id = "" } = useParams();
  const app = useQuery({ queryKey: ["apps", id], queryFn: () => api.apps.get(id) });
  const targets = useQuery({ queryKey: ["apps-targets"], queryFn: api.apps.targets });
  const a = app.data;
  const hero = <PageHero icon={Package} title={a?.name ?? "App"} description={a?.summary ?? "Loading…"} action={a ? <CatalogAppLogo logo={a.logo} name={a.name} size="lg" /> : undefined} />;
  if (app.error) return <div className="flex flex-col lg:px-[13px]">{hero}<div className="hd-card mt-[15px] p-6"><ErrorState error={app.error} onRetry={() => void app.refetch()} /></div></div>;
  if (!a || !targets.data) return <div className="flex flex-col lg:px-[13px]">{hero}<Skeleton className="mt-[15px] h-96 w-full rounded-xl" /></div>;
  return (
    <div className="flex flex-col lg:px-[13px]">
      {hero}
      <div className="mt-[15px] grid items-start gap-[15px] xl:grid-cols-[minmax(0,1fr)_360px]">
        <InstallForm a={a} t={targets.data} />
        <aside className="flex flex-col gap-[15px]">
          <DetailCard title="About">
            <p className="text-[12.5px] leading-[19px] text-muted">{a.description}</p>
            <KeyValue items={[
              { label: "Category", value: CATEGORY_LABEL[a.category] ?? a.category },
              { label: "Release", value: a.versions[0] ? `${a.versions[0].version}` : "—" },
              { label: "Licence", value: `${a.license.name}${a.license.osi ? " (open source)" : ""}` },
              { label: "Website", value: <a className="inline-flex items-center gap-1 text-accent hover:underline" href={a.website} target="_blank" rel="noreferrer">{new URL(a.website).hostname}<ExternalLink size={11} /></a> },
            ]} />
          </DetailCard>
          <DetailCard title="Requirements and resources">
            <KeyValue items={[
              { label: "PHP", value: a.requirements.phpMin ? `${a.requirements.phpMin}${a.requirements.phpMax ? ` to ${a.requirements.phpMax}` : " or newer"}` : "—" },
              { label: "Database", value: a.requirements.database ? "One MariaDB database (created for it, counts toward your plan)" : "None" },
              { label: "Memory", value: `${a.requirements.memoryMb} MB from your package` },
              { label: "Disk", value: `About ${a.requirements.diskMb} MB` },
            ]} />
          </DetailCard>
          <DetailCard title="Managed by HostDock">
            <ul className="flex flex-col gap-1.5 text-[12.5px]">
              {a.capabilities.filter((c) => c !== "install" && CAP_LABEL[c]).map((c) => <li key={c} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-success" />{CAP_LABEL[c]}</li>)}
              {!a.capabilities.includes("update") && <li className="flex items-start gap-2 text-muted"><Info size={14} className="mt-0.5 shrink-0" />Updates are applied from inside {a.name}; HostDock does not update it yet.</li>}
            </ul>
            {a.recovery && <p className="mt-3 text-[12px] leading-[18px] text-muted">{a.recovery}</p>}
          </DetailCard>
        </aside>
      </div>
    </div>
  );
}

function InstallForm({ a, t }: { a: AppCatalogItem; t: AppInstallTarget }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { session } = useSession();
  const [version, setVersion] = useState(a.versions[0]?.version ?? "");
  const minPhp = a.versions.find((v) => v.version === version)?.phpMin ?? a.requirements.phpMin;
  const sites = t.websites.filter((w) => w.status === "ACTIVE" && w.appType === "PHP");
  const canExisting = a.locations.root || a.locations.subdirectory;
  const [kind, setKind] = useState<"new" | "existing">(a.locations.newWebsite ? "new" : "existing");
  const [subscriptionId, setSubscriptionId] = useState(t.subscriptions[0]?.id ?? "");
  const sub = t.subscriptions.find((s) => s.id === subscriptionId);
  const phpChoices = (sub?.phpVersions ?? []).filter((v) => phpOk(a, v, minPhp));
  const [php, setPhp] = useState("");
  const [domain, setDomain] = useState(t.domains[0]?.name ?? "");
  const [websiteId, setWebsiteId] = useState(sites[0]?.id ?? "");
  const site = sites.find((w) => w.id === websiteId);
  const [where, setWhere] = useState<"root" | "folder">(a.locations.subdirectory ? "folder" : "root");
  const [folder, setFolder] = useState(a.id === "wordpress" || a.id === "woocommerce" ? "" : a.id.replace(/[^a-z0-9]/g, "").slice(0, 12));
  const defaults = useMemo(() => Object.fromEntries(a.config.map((f) => [f.key, f.kind === "email" ? session.user.email : f.default ?? ""])), [a, session.user.email]);
  const [values, setValues] = useState<Record<string, string>>(defaults);
  const [jobId, setJobId] = useState<string | null>(null);
  const [installationId, setInstallationId] = useState<string | null>(null);
  const [generated, setGenerated] = useState<string[]>([]);
  const { job, logs } = useLiveJob(jobId);

  const path = kind === "existing" && where === "folder" ? folder.trim().replace(/^\/+|\/+$/g, "") : "";
  const host = kind === "new" ? domain : site?.domain ?? "";
  const url = host ? `${host}${path ? `/${path}` : ""}` : "";
  const install = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.apps.install(a.id, {
      version,
      target: kind === "new" ? { kind, subscriptionId, domain, ...(php ? { runtimeVersion: php } : {}) } : { kind, websiteId, path },
      config: Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== "")),
    }),
    onSuccess: (r) => {
      setJobId(r.job.id); setInstallationId(r.installation.id); setGenerated(r.generated);
      void qc.invalidateQueries({ queryKey: ["app-installations"] });
      void qc.invalidateQueries({ queryKey: ["websites"] });
      void qc.invalidateQueries({ queryKey: ["domains"] });
    },
  });
  const fe = install.error instanceof ApiError ? install.error.fieldErrors : {};

  if (jobId) return <InstallProgress a={a} job={job} logs={logs.map((l) => l.message)} installationId={installationId} url={url} generated={generated} onDone={() => installationId && navigate(`/apps/installations/${installationId}`)} />;

  const siteOk = kind === "new" ? Boolean(domain && sub && phpChoices.length) : Boolean(site && phpOk(a, site.runtimeVersion, minPhp) && (where === "root" || /^[a-z0-9][a-z0-9_-]{0,39}(\/[a-z0-9][a-z0-9_-]{0,39}){0,2}$/.test(path)));
  const required = a.config.filter((f) => f.required && !f.generate).every((f) => (values[f.key] ?? "").trim());
  return (
    <DetailCard title={`Install ${a.name}`} description="Nothing on your websites is overwritten: HostDock installs only into a new website or an empty folder.">
      {!a.compatibility.installable && (
        <Banner className="!mt-0 mb-4" icon={TriangleAlert} title="This app cannot be installed right now">
          <ul className="list-disc pl-4">{a.compatibility.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
        </Banner>
      )}
      <div className="flex flex-col gap-5">
        <section className="flex flex-col gap-2">
          <h3 className="text-[13px] font-semibold">Where</h3>
          <div className="grid gap-2 sm:grid-cols-2">
            {a.locations.newWebsite && <Choice selected={kind === "new"} onClick={() => setKind("new")} title="On a new website" description="A new website on one of your unused domains or subdomains." />}
            {canExisting && <Choice selected={kind === "existing"} disabled={!sites.length} onClick={() => setKind("existing")} title="On an existing website" description={sites.length ? (a.locations.subdirectory ? "At its root or in a folder, e.g. example.com/forum." : "At the root of a website with no files yet.") : "You have no active PHP website."} />}
          </div>
          {kind === "new" && (
            <div className="grid gap-3 sm:grid-cols-2">
              {t.subscriptions.length > 1 && (
                <Field label="Hosting package">{(p) => <Select {...p} value={subscriptionId} onChange={(e) => setSubscriptionId(e.target.value)}>{t.subscriptions.map((s) => <option key={s.id} value={s.id}>{s.planName}</option>)}</Select>}</Field>
              )}
              <Field label="Domain" hint={t.domains.length ? "An unused domain from your list." : undefined} error={fe["target.domain"]}>
                {(p) => t.domains.length ? (
                  <Select {...p} value={domain} onChange={(e) => setDomain(e.target.value)}>{t.domains.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}</Select>
                ) : <p className="text-[12.5px] text-muted">No unused domains. <Link to="/domains" className="text-accent hover:underline">Add a domain or subdomain</Link> first.</p>}
              </Field>
              <Field label="PHP version" hint={phpChoices.length ? undefined : `Your plan has no PHP version ${a.name} supports.`}>
                {(p) => <Select {...p} value={php || phpChoices[0] || ""} onChange={(e) => setPhp(e.target.value)}>{phpChoices.map((v) => <option key={v} value={v}>PHP {v}</option>)}</Select>}
              </Field>
            </div>
          )}
          {kind === "existing" && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Website">{(p) => <Select {...p} value={websiteId} onChange={(e) => setWebsiteId(e.target.value)}>{sites.map((w) => <option key={w.id} value={w.id}>{w.domain} (PHP {w.runtimeVersion})</option>)}</Select>}</Field>
              {a.locations.root && a.locations.subdirectory && (
                <Field label="Location">{(p) => <Select {...p} value={where} onChange={(e) => setWhere(e.target.value as "root" | "folder")}><option value="folder">In a folder</option><option value="root">At the website root</option></Select>}</Field>
              )}
              {where === "folder" && a.locations.subdirectory && (
                <Field label="Folder" hint="Lowercase letters, digits, dash or underscore. It must be new or empty." error={fe["target.path"]}>
                  {(p) => <Input {...p} value={folder} onChange={(e) => setFolder(e.target.value.toLowerCase())} placeholder="blog" />}
                </Field>
              )}
              {site && !phpOk(a, site.runtimeVersion, minPhp) && <Notice tone="warning">This website runs PHP {site.runtimeVersion}; {a.name} needs PHP {minPhp}{a.requirements.phpMax ? ` to ${a.requirements.phpMax}` : " or newer"}. Change its PHP version first.</Notice>}
            </div>
          )}
          {url && <p className="text-[12.5px] text-muted">Address: <span className="font-medium text-fg">{url}</span>. HTTPS starts automatically once the domain's DNS points to HostDock.</p>}
        </section>

        {a.versions.length > 1 && (
          <Field label="Version">{(p) => <Select {...p} value={version} onChange={(e) => setVersion(e.target.value)}>{a.versions.map((v, i) => <option key={v.version} value={v.version}>{v.version}{i === 0 ? " (latest approved)" : ""}</option>)}</Select>}</Field>
        )}

        {a.config.length > 0 && (
          <section className="flex flex-col gap-3">
            <h3 className="text-[13px] font-semibold">Settings</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {a.config.map((f) => <ConfigField key={f.key} f={f} value={values[f.key] ?? ""} error={fe[`config.${f.key}`]} onChange={(v) => setValues({ ...values, [f.key]: v })} />)}
            </div>
            {a.capabilities.includes("adminLogin") && <Notice>You sign in to {a.name} from HostDock with a single-use link, so there is no administrator password to remember.</Notice>}
          </section>
        )}

        <KeyValue items={[
          { label: "Release", value: `${a.name} ${version}, verified against ${a.versions.find((v) => v.version === version)?.checksumSource ?? "the vendor checksum"}` },
          { label: "Database", value: a.requirements.database ? "A new MariaDB database just for this app" : "None" },
          { label: "Resources", value: `Runs in your package's shared CPU and memory (needs about ${a.requirements.memoryMb} MB) and uses about ${a.requirements.diskMb} MB of disk` },
        ]} />
        {install.error && <FormError error={install.error} />}
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button variant="ghost" onClick={() => navigate("/apps")}>Cancel</Button>
          <Button variant="primary" loading={install.isPending} disabled={!a.compatibility.installable || !siteOk || !required} onClick={() => install.mutate()}>Install {a.name}</Button>
        </div>
      </div>
    </DetailCard>
  );
}

function ConfigField({ f, value, error, onChange }: { f: AppConfigFieldInfo; value: string; error?: string; onChange(v: string): void }) {
  const hint = f.help ?? (f.generate === "username" ? "Leave empty to generate one." : f.generate === "password" ? "Leave empty to generate a strong password, shown to you once." : undefined);
  return (
    <Field label={`${f.label}${f.required && !f.generate ? "" : " (optional)"}`} hint={hint} error={error}>
      {(p) => <Input {...p} type={f.kind === "password" ? "password" : f.kind === "email" ? "email" : "text"} autoComplete={f.kind === "password" ? "new-password" : "off"} value={value} onChange={(e) => onChange(e.target.value)} />}
    </Field>
  );
}

const STEPS: [RegExp, string][] = [
  [/release|Checking the release/, "Check the release"],
  [/website|Creating the website|Placed|account/, "Prepare the website"],
  [/Compatibility|compatibility|quotas/, "Check compatibility and quotas"],
  [/database|Database/, "Create the database"],
  [/Download|Verified/, "Download and verify"],
  [/Extracted|extract/, "Extract into an empty folder"],
  [/Configur|installer|Joomla|YOURLS|WordPress/, "Configure the application"],
  [/Health|health|Checking the application/, "Check that it answers"],
];

function InstallProgress({ a, job, logs, installationId, url, generated, onDone }: { a: AppCatalogItem; job: { status: string; progress: number; progressMessage: string | null; error: string | null } | null; logs: string[]; installationId: string | null; url: string; generated: string[]; onDone(): void }) {
  const done = job?.status === "SUCCESS";
  const failed = job?.status === "FAILED";
  const text = [...logs, job?.progressMessage ?? ""].join("\n");
  const reached = STEPS.map(([re]) => re.test(text));
  const last = reached.lastIndexOf(true);
  return (
    <DetailCard title={done ? `${a.name} is installed` : failed ? "The installation stopped" : `Installing ${a.name}…`}>
      <div className="flex flex-col gap-4" aria-live="polite">
        <div className="flex items-center gap-3">
          {done ? <CheckCircle2 className="text-success" /> : failed ? <XCircle className="text-danger" /> : <Loader2 className="animate-spin text-accent" />}
          <p className="text-[13px] text-muted">{done ? `${url} is ready.` : failed ? job?.error : job?.progressMessage ?? "Queued"}</p>
        </div>
        <div className="h-[7px] overflow-hidden rounded-full bg-surface-3" role="progressbar" aria-valuenow={job?.progress ?? 0} aria-valuemin={0} aria-valuemax={100}>
          <div className={cn("h-full rounded-full transition-all", failed ? "bg-danger" : done ? "bg-[#14a04a]" : "bg-[#048d9f]")} style={{ width: `${done ? 100 : job?.progress ?? 2}%` }} />
        </div>
        <ul className="flex flex-col gap-1.5">
          {STEPS.map(([, label], i) => {
            const state = done || i < last ? "done" : i === last && !failed ? "active" : failed && i === last ? "failed" : "pending";
            return (
              <li key={label} className="flex items-center gap-2 text-[13px]">
                {state === "done" ? <CheckCircle2 size={15} className="text-success" /> : state === "active" ? <Loader2 size={15} className="animate-spin text-accent" /> : state === "failed" ? <XCircle size={15} className="text-danger" /> : <span className="h-[15px] w-[15px] rounded-full border border-line-strong" />}
                <span className={state === "pending" ? "text-subtle" : undefined}>{label}</span>
              </li>
            );
          })}
        </ul>
        {failed && <Notice tone="warning">HostDock removed what this attempt created (files, database{a.locations.newWebsite ? ", and a website made for it" : ""}). Your other files were not touched.</Notice>}
        {done && generated.some((g) => a.config.find((f) => f.key === g)?.secret) && <Notice tone="warning">A password was generated for you. Open the installation to see it — it is shown only once.</Notice>}
        {(done || failed) && installationId && <div className="flex gap-2"><Button variant="primary" onClick={onDone}>{done ? "Manage installation" : "See details"}</Button>{done && <a href={`http://${url}/`} target="_blank" rel="noreferrer"><Button>Open site</Button></a>}</div>}
        {!done && !failed && <p className="text-[12px] text-muted">You can leave this page; the installation continues and appears under <Link to="/apps?tab=installed" className="text-accent hover:underline">My installations</Link>.</p>}
        {done && <Pill tone="ok" dot>Health check passed</Pill>}
      </div>
    </DetailCard>
  );
}
